import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Row,
  Col,
  Spinner,
  Alert,
  Modal,
  Pagination,
  Badge,
} from 'react-bootstrap';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { useHighlightedStores } from '../../context/HighlightedStoresContext';
import api, { getApiErrorMessage } from '../../services/api';
import { useOrdersWebSocket } from '../../hooks/useOrdersWebSocket';

const ACCEPTANCE_MODE_STORAGE_KEY = 'adminOrdersAcceptanceMode';

function readAcceptanceModeDefault() {
  try {
    const v = localStorage.getItem(ACCEPTANCE_MODE_STORAGE_KEY);
    return v === null ? true : v === 'true';
  } catch {
    return true;
  }
}

function wbCatalogUrlFromNmId(nm) {
  if (nm == null || nm === '') return null;
  const n = Number(nm);
  if (Number.isNaN(n)) return null;
  return `https://www.wildberries.ru/catalog/${n}/detail.aspx`;
}

function wbAdminCatalogUrl(item) {
  if (!item) return null;
  const u = (item.wb_product_url || '').trim();
  if (u) return u;
  const fromNm = wbCatalogUrlFromNmId(item.wb_nm_id);
  if (fromNm) return fromNm;
  const link = (item.sku_link || '').trim();
  return link || null;
}

function formatAdminItemDimsWeight(item, t) {
  if (!item) return t('returns.dimensionsWeightUnavailable');
  const L = item.sku_length;
  const W = item.sku_width;
  const H = item.sku_height;
  const hasDims =
    (L != null && Number(L) > 0) || (W != null && Number(W) > 0) || (H != null && Number(H) > 0);
  const wkgRaw = item.sku_weight_kg;
  const wkg = wkgRaw != null && wkgRaw !== '' ? Number(wkgRaw) : NaN;
  const hasWeight = !Number.isNaN(wkg) && wkg > 0;
  if (!hasDims && !hasWeight) return t('returns.dimensionsWeightUnavailable');
  const dStr = hasDims
    ? `${L || 0}×${W || 0}×${H || 0} ${t('returns.unitCm')}`
    : t('returns.dimensionsUnknownShort');
  const wStr = hasWeight ? `${wkg} ${t('returns.unitKg')}` : t('returns.weightUnknownShort');
  return `${dStr} · ${wStr}`;
}

const STORE_MISSING_REMARK = 'Данный магазин отсутствует в системе!';

/** Колонка магазина / seller_id в админ-таблице и в деталях заказа. */
function StoreSellerCell({ order }) {
  if (!order?.seller_id && !order?.store_name) {
    return <>-</>;
  }
  return (
    <div>
      {order.store_name ? (
        <>
          <div style={{ fontWeight: 'bold' }}>{order.store_name}</div>
          <small style={{ opacity: 0.7 }}>{order.seller_id}</small>
        </>
      ) : (
        <span>{order.seller_id || '-'}</span>
      )}
      {order.store_missing_in_system && (
        <div className="fw-bold text-danger small mt-1">{STORE_MISSING_REMARK}</div>
      )}
    </div>
  );
}

/** Валидация полей позиции перед приёмкой (должно совпадать с item_receipt_metrics_complete на бэкенде). */
function receiptMetricsFormValid(byId) {
  const rows = Object.values(byId);
  if (rows.length === 0) return false;
  return rows.every((r) => {
    const name = (r.sku_name || '').trim();
    const L = parseInt(r.sku_length, 10);
    const W = parseInt(r.sku_width, 10);
    const H = parseInt(r.sku_height, 10);
    const w = parseFloat(String(r.sku_weight_kg).replace(',', '.'));
    return (
      name.length > 0
      && !Number.isNaN(L) && L > 0
      && !Number.isNaN(W) && W > 0
      && !Number.isNaN(H) && H > 0
      && !Number.isNaN(w) && w > 0
    );
  });
}

function receiptMetricFieldEmpty(v) {
  if (v == null) return true;
  return String(v).trim() === '';
}

/** Начальные поля приёмки из уже загруженного заказа (без ожидания WB). */
function seedReceiptMetricsFromOrderItems(order) {
  const byId = {};
  (order?.items || []).forEach((it) => {
    byId[it.id] = {
      id: it.id,
      sku_name: it.sku_name != null ? String(it.sku_name) : '',
      sku_length: it.sku_length != null && it.sku_length !== '' ? String(it.sku_length) : '',
      sku_width: it.sku_width != null && it.sku_width !== '' ? String(it.sku_width) : '',
      sku_height: it.sku_height != null && it.sku_height !== '' ? String(it.sku_height) : '',
      sku_weight_kg: it.sku_weight_kg != null && it.sku_weight_kg !== ''
        ? String(it.sku_weight_kg)
        : '',
      nm_id: it.wb_nm_id != null && it.wb_nm_id !== '' ? it.wb_nm_id : null,
      enriched_from_wb: false,
      metrics_complete: false,
    };
  });
  return byId;
}

/** Подмешать ответ preflight: не затираем поля, которые сотрудник уже заполнил. */
function mergeReceiptWbPreflightIntoMetrics(prev, preflightData) {
  const items = preflightData?.items || [];
  if (!items.length) return prev;
  const next = { ...prev };
  items.forEach((row) => {
    const p = next[row.id];
    const pickStr = (userVal, apiVal) => {
      if (!receiptMetricFieldEmpty(userVal)) return userVal;
      return apiVal != null && apiVal !== '' ? String(apiVal) : '';
    };
    const base = p || {
      id: row.id,
      sku_name: '',
      sku_length: '',
      sku_width: '',
      sku_height: '',
      sku_weight_kg: '',
      nm_id: null,
      enriched_from_wb: false,
      metrics_complete: false,
    };
    next[row.id] = {
      ...base,
      nm_id: base.nm_id != null && base.nm_id !== '' ? base.nm_id : (row.nm_id ?? null),
      sku_name: pickStr(base.sku_name, row.sku_name),
      sku_length: pickStr(base.sku_length, row.sku_length),
      sku_width: pickStr(base.sku_width, row.sku_width),
      sku_height: pickStr(base.sku_height, row.sku_height),
      sku_weight_kg: pickStr(base.sku_weight_kg, row.sku_weight_kg),
      metrics_complete: !!row.metrics_complete,
      enriched_from_wb: base.enriched_from_wb || !!row.enriched_from_wb,
    };
  });
  return next;
}

function downloadBase64Pdf(base64, filename) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'label.pdf';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const SALE_BARCODE_STATUSES = ['transferring_to_sale', 'submitted_for_sale'];

function orderItemNeedsSkuBarcode(item) {
  return !(item?.sku_barcode || '').trim();
}

function orderNeedsSkuBarcode(order) {
  return (order?.items || []).some(orderItemNeedsSkuBarcode);
}

function preflightNeedsBarcodeModal(preflight) {
  return (preflight?.items || []).some(
    (row) => row.needs_selection || row.needs_manual_barcode,
  );
}

function preflightBarcodeModalRows(preflight) {
  return (preflight?.items || []).filter(
    (row) => row.needs_selection || row.needs_manual_barcode,
  );
}

function WbCardSummaryPanel({ wbCard, searchKey }) {
  if (!wbCard) {
    return (
      <Alert variant="warning" className="py-2 small mb-2">
        Карточка WB не найдена
        {searchKey ? ` (поиск: ${searchKey})` : ''}
        .
      </Alert>
    );
  }
  const dims = [wbCard.length_cm, wbCard.width_cm, wbCard.height_cm].filter((v) => v > 0);
  return (
    <div className="small border rounded p-2 mb-2 bg-body-tertiary">
      <div className="fw-semibold mb-1">Данные карточки WB</div>
      <div><strong>nmID:</strong> {wbCard.nm_id ?? '—'}</div>
      {wbCard.vendor_code ? <div><strong>Артикул:</strong> {wbCard.vendor_code}</div> : null}
      {wbCard.brand ? <div><strong>Бренд:</strong> {wbCard.brand}</div> : null}
      {wbCard.title ? <div><strong>Название:</strong> {wbCard.title}</div> : null}
      {wbCard.subject_name ? <div><strong>Предмет:</strong> {wbCard.subject_name}</div> : null}
      {dims.length === 3 ? (
        <div>
          <strong>Габариты (см):</strong>
          {' '}
          {wbCard.length_cm}×{wbCard.width_cm}×{wbCard.height_cm}
        </div>
      ) : null}
      {wbCard.weight_kg != null ? (
        <div><strong>Вес (кг):</strong> {wbCard.weight_kg}</div>
      ) : null}
      {searchKey ? (
        <div className="text-muted mt-1">Поиск в Content API: {searchKey}</div>
      ) : null}
      {(wbCard.sizes || []).length > 0 ? (
        <Table size="sm" bordered responsive className="mb-0 mt-2 bg-body">
          <thead>
            <tr>
              <th>chrtID</th>
              <th>techSize</th>
              <th>wbSize</th>
              <th>skus / штрихкоды</th>
            </tr>
          </thead>
          <tbody>
            {wbCard.sizes.map((sz, idx) => (
              <tr key={`${sz.chrt_id ?? 'x'}-${idx}`}>
                <td>{sz.chrt_id ?? '—'}</td>
                <td>{sz.tech_size || '—'}</td>
                <td>{sz.wb_size || '—'}</td>
                <td className="font-monospace">
                  {(sz.skus || []).length > 0 ? sz.skus.join(', ') : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <div className="text-muted mt-1">В карточке нет размеров (sizes[]).</div>
      )}
    </div>
  );
}

const AdminOrdersPage = ({ initialEditOrderId, onClearInitialEditOrderId }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isOrderRowHighlighted } = useHighlightedStores();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pageErrorText = typeof error === 'string' ? error.trim() : '';
  const [orders, setOrders] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  
  // User filter from URL (for viewing specific user's orders from support chat)
  // Initialize directly from URL to avoid double-fetch
  const [userIdFilter, setUserIdFilter] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('user_id') || null;
  });
  const [filterUserInfo, setFilterUserInfo] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [pageSize, setPageSize] = useState(100);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [returnStatusFilter, setReturnStatusFilter] = useState('');
  const [sellerIdFilter, setSellerIdFilter] = useState('');
  const [transitionDateFrom, setTransitionDateFrom] = useState('');
  const [transitionDateTo, setTransitionDateTo] = useState('');
  const [transitionTargetStatuses, setTransitionTargetStatuses] = useState([]);
  const [acceptanceMode, setAcceptanceMode] = useState(readAcceptanceModeDefault);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editModalError, setEditModalError] = useState('');
  const [labelGeneratingOrderId, setLabelGeneratingOrderId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Attachment modal state
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [attachmentOrderItem, setAttachmentOrderItem] = useState(null);
  const [attachmentType, setAttachmentType] = useState('photo');
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  
  // Photo lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxPhotos, setLightboxPhotos] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openWbProductLightbox = useCallback((imageUrl) => {
    if (!imageUrl) return;
    const payload = { file_url: imageUrl, file_name: 'Wildberries' };
    setLightboxPhotos([payload]);
    setLightboxIndex(0);
    setLightboxPhoto(payload);
  }, []);
  
  // Receipt modal: when transitioning from awaiting_receipt to any other — choose warehouse vs pickup and amount
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  /** Старый UI: 2 фиксированных варианта + свой тариф. */
  const [receiptVariant, setReceiptVariant] = useState('warehouse_default');
  const [receiptType, setReceiptType] = useState('receipt_warehouse');
  const [receiptAmount, setReceiptAmount] = useState('2');
  const [receiptSaving, setReceiptSaving] = useState(false);
  const [receiptPreflightLoading, setReceiptPreflightLoading] = useState(false);
  const [receiptPreflightError, setReceiptPreflightError] = useState('');
  const [receiptGoodsReturnSync, setReceiptGoodsReturnSync] = useState(null);
  const [receiptMetricsById, setReceiptMetricsById] = useState({});
  const receiptPreflightAbortRef = useRef(null);
  const handleSearchRef = useRef(() => {});
  // Prepare return modal: when setting status to sent_to_seller — confirm/edit amount (3.5 per item)
  const [showPrepareReturnModal, setShowPrepareReturnModal] = useState(false);
  const [prepareReturnAmount, setPrepareReturnAmount] = useState('3.5');
  const [prepareReturnSaving, setPrepareReturnSaving] = useState(false);
  // Barcode selection before sale transfer / WH label
  const [showSaleBarcodeModal, setShowSaleBarcodeModal] = useState(false);
  const [saleBarcodePreflight, setSaleBarcodePreflight] = useState(null);
  const [saleBarcodeSelections, setSaleBarcodeSelections] = useState({});
  const [saleBarcodePreflightLoading, setSaleBarcodePreflightLoading] = useState(false);
  const [saleBarcodeSaving, setSaleBarcodeSaving] = useState(false);
  const [saleBarcodePreflightError, setSaleBarcodePreflightError] = useState('');
  const [pendingSaleAction, setPendingSaleAction] = useState(null);
  const [pendingLabelOrder, setPendingLabelOrder] = useState(null);
  // Billing modal: show created expenses when order is set to "sent to seller", allow edit
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingExpenses, setBillingExpenses] = useState([]);
  const [billingOrderNo, setBillingOrderNo] = useState('');
  const [editingBillingExpense, setEditingBillingExpense] = useState(null);
  const [billingEditAmount, setBillingEditAmount] = useState('');
  const [billingEditReason, setBillingEditReason] = useState('');
  const [billingSaveError, setBillingSaveError] = useState('');
  const [billingSaving, setBillingSaving] = useState(false);
  const [wbRefreshingOrderId, setWbRefreshingOrderId] = useState(null);
  const [adminWbThumbNotFoundByItemId, setAdminWbThumbNotFoundByItemId] = useState({});
  const [adminWbMetricsDoneByItemId, setAdminWbMetricsDoneByItemId] = useState({});

  const adminMissingWbThumbKey = useMemo(() => {
    const ids = [];
    for (const o of orders || []) {
      for (const it of o.items || []) {
        if (
          it.id &&
          !String(it.wb_primary_image_url || '').trim() &&
          !adminWbThumbNotFoundByItemId[it.id]
        ) {
          ids.push(it.id);
        }
      }
    }
    return ids.sort((a, b) => a - b).join(',');
  }, [orders, adminWbThumbNotFoundByItemId]);

  const adminMissingWbMetricsKey = useMemo(() => {
    const ids = [];
    for (const o of orders || []) {
      for (const it of o.items || []) {
        if (!it?.id || adminWbMetricsDoneByItemId[it.id]) continue;
        const hasDims = Number(it.sku_length) > 0 && Number(it.sku_width) > 0 && Number(it.sku_height) > 0;
        const w = it.sku_weight_kg != null && it.sku_weight_kg !== '' ? Number(it.sku_weight_kg) : NaN;
        const hasWeight = !Number.isNaN(w) && w > 0;
        if (!hasDims || !hasWeight) ids.push(it.id);
      }
    }
    return ids.sort((a, b) => a - b).join(',');
  }, [orders, adminWbMetricsDoneByItemId]);

  const mergeAdminWbMediaChunk = useCallback((rows) => {
    const byItemId = new Map(rows.map((x) => [x.order_item_id, x]));
    const patchItem = (it) => {
      const u = byItemId.get(it.id);
      if (!u) return it;
      if (u.found && u.wb_primary_image_url) {
        return {
          ...it,
          wb_primary_image_url: u.wb_primary_image_url,
          wb_primary_image_big_url: u.wb_primary_image_big_url || u.wb_primary_image_url,
          wb_product_url: it.wb_product_url || wbCatalogUrlFromNmId(it.wb_nm_id),
        };
      }
      return it;
    };
    setOrders((prev) =>
      prev.map((o) => ({
        ...o,
        items: (o.items || []).map(patchItem),
      })),
    );
    setEditingOrder((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: (prev.items || []).map(patchItem),
      };
    });
    setAdminWbThumbNotFoundByItemId((prev) => {
      const next = { ...prev };
      for (const x of rows) {
        if (!x.order_item_id) continue;
        if (x.found && x.wb_primary_image_url) delete next[x.order_item_id];
        else if (!x.found) next[x.order_item_id] = true;
      }
      return next;
    });
  }, []);

  const mergeAdminWbMetricsChunk = useCallback((rows) => {
    const byItemId = new Map(rows.map((x) => [x.order_item_id, x]));
    const patchItem = (it) => {
      const u = byItemId.get(it.id);
      if (!u) return it;
      const nextNm = u.wb_nm_id ?? it.wb_nm_id;
      return {
        ...it,
        sku_name: u.sku_name || it.sku_name,
        sku_length: u.sku_length || it.sku_length,
        sku_width: u.sku_width || it.sku_width,
        sku_height: u.sku_height || it.sku_height,
        sku_weight_kg: u.sku_weight_kg != null ? u.sku_weight_kg : it.sku_weight_kg,
        wb_primary_image_url: u.wb_primary_image_url || it.wb_primary_image_url,
        wb_primary_image_big_url: u.wb_primary_image_big_url || it.wb_primary_image_big_url,
        wb_nm_id: nextNm,
        wb_product_url: it.wb_product_url || wbCatalogUrlFromNmId(nextNm),
      };
    };
    setOrders((prev) =>
      prev.map((o) => ({
        ...o,
        items: (o.items || []).map(patchItem),
      })),
    );
    setEditingOrder((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: (prev.items || []).map(patchItem),
      };
    });
    setAdminWbMetricsDoneByItemId((prev) => {
      const next = { ...prev };
      for (const row of rows || []) {
        const iid = row?.order_item_id;
        if (!iid) continue;
        next[iid] = true;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!user?.is_superuser || loading || !adminMissingWbThumbKey) return undefined;
    const ids = adminMissingWbThumbKey
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
    if (!ids.length) return undefined;
    let cancelled = false;
    const delay = (ms) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      try {
        for (let i = 0; i < ids.length; i += 10) {
          if (cancelled) return;
          const chunk = ids.slice(i, i + 10);
          try {
            const res = await api.postReturnsOrderItemsWbMedia(chunk);
            if (!cancelled) mergeAdminWbMediaChunk(res.items || []);
          } catch (e) {
            console.error(e);
          }
          if (i + 10 < ids.length && !cancelled) await delay(500);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.is_superuser, loading, adminMissingWbThumbKey, mergeAdminWbMediaChunk]);

  useEffect(() => {
    if (!user?.is_superuser || loading || !adminMissingWbMetricsKey) return undefined;
    const ids = adminMissingWbMetricsKey
      .split(',')
      .map((s) => parseInt(s, 10))
      .filter((n) => !Number.isNaN(n) && n > 0);
    if (!ids.length) return undefined;
    let cancelled = false;
    const delay = (ms) => new Promise((r) => setTimeout(r, ms));
    (async () => {
      try {
        for (let i = 0; i < ids.length; i += 10) {
          if (cancelled) return;
          const chunk = ids.slice(i, i + 10);
          try {
            const res = await api.postAdminOrderItemsWbMetrics(chunk);
            if (!cancelled) mergeAdminWbMetricsChunk(res.items || []);
          } catch (e) {
            console.error(e);
          }
          if (i + 10 < ids.length && !cancelled) await delay(500);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.is_superuser, loading, adminMissingWbMetricsKey, mergeAdminWbMetricsChunk]);

  // Status choices
  const [statusChoices, setStatusChoices] = useState([]);
  
  // Get allowed statuses based on current status
  // Admin can change to any status in ADMIN_ALLOWED_STATUSES from backend
  // But we limit UI choices to logical transitions for better UX
  const getAllowedStatuses = (currentStatus) => {
    const statusMap = {
      'awaiting_receipt': [
        'received_awaiting_check',
        'awaiting_seller_decision',
        'awaiting_for_sale',
        'transferring_to_sale',
        'submitted_for_sale',
      ],
      'received_awaiting_check': [
        'awaiting_seller_decision',
        'awaiting_for_sale',
        'transferring_to_sale',
        'submitted_for_sale',
      ],
      'awaiting_seller_decision': ['awaiting_for_sale', 'awaiting_disposal', 'awaiting_shipment'],
      'awaiting_for_sale': ['transferring_to_sale', 'submitted_for_sale'],
      'transferring_to_sale': ['submitted_for_sale'],
      'awaiting_shipment': ['sent_to_seller'],
      'awaiting_disposal': ['disposed'],
      // Allow admin to set any allowed status from these starting points
      'submitted_for_sale': ['awaiting_disposal', 'disposed'],
      'sent_to_seller': [],
      'disposed': [],
    };
    return statusMap[currentStatus] || [];
  };

  // Status translations - use API status display or fallback
  const getStatusLabel = (statusValue) => {
    const statusChoice = statusChoices.find(s => s.value === statusValue);
    if (statusChoice) return statusChoice.display;
    return statusValue;
  };

  // Get status badge color and style
  const getStatusBadgeProps = (status) => {
    // Statuses that require action - use bright colors
    const actionRequiredStatuses = [
      'received_awaiting_check',
      'awaiting_for_sale',
      'transferring_to_sale',
      'awaiting_shipment',
      'awaiting_disposal',
    ];

    if (actionRequiredStatuses.includes(status)) {
      const colorMap = {
        'received_awaiting_check': { bg: 'danger', text: 'white' }, // Red
        'awaiting_for_sale': { bg: 'warning', text: 'dark' }, // Orange/Yellow
        'transferring_to_sale': { bg: 'info', text: 'white' }, // Blue — промежуточный
        'awaiting_shipment': { bg: 'info', text: 'white' }, // Blue
        'awaiting_disposal': { bg: 'danger', text: 'white' }, // Red
      };
      return colorMap[status] || { bg: 'secondary', text: 'white' };
    }
    
    return { bg: 'secondary', text: 'white' };
  };

  // Load status choices
  useEffect(() => {
    const loadStatusChoices = async () => {
      try {
        const response = await api.request('/orders/status-choices/', { method: 'GET' });
        if (response.data) {
          setStatusChoices(response.data);
        }
      } catch (err) {
        console.error('Failed to load status choices:', err);
      }
    };
    loadStatusChoices();
  }, []);

  // При открытии модалки приёмки — сразу показываем поля из заказа; WB preflight в фоне (не блокируем ввод).
  useEffect(() => {
    if (!showReceiptModal || !editingOrder?.id) return undefined;
    let cancelled = false;
    const ac = new AbortController();
    receiptPreflightAbortRef.current = ac;

    setReceiptPreflightLoading(true);
    setReceiptPreflightError('');
    setReceiptGoodsReturnSync(null);
    setReceiptMetricsById(seedReceiptMetricsFromOrderItems(editingOrder));

    api
      .postAdminOrderReceiptWbPreflight(editingOrder.id, { signal: ac.signal })
      .then((data) => {
        if (cancelled) return;
        if (typeof data.goods_return_sync === 'boolean') {
          setReceiptGoodsReturnSync(data.goods_return_sync);
        }
        setReceiptMetricsById((prev) => mergeReceiptWbPreflightIntoMetrics(prev, data));
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.name === 'AbortError') return;
        setReceiptPreflightError(
          err?.data?.error || err?.message || 'Ошибка загрузки данных WB',
        );
      })
      .finally(() => {
        if (!cancelled) setReceiptPreflightLoading(false);
      });

    return () => {
      cancelled = true;
      ac.abort();
      receiptPreflightAbortRef.current = null;
    };
  }, [showReceiptModal, editingOrder?.id, editingOrder?.items?.length]);

  // Load orders (options.page overrides currentPage — нужно для поиска с page=1 до обновления state)
  const loadOrders = useCallback(async (options = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = {
        page: options.page ?? currentPage,
        page_size: pageSize,
      };
      
      const effectiveSearch = (options.search ?? searchQuery).trim();
      if (effectiveSearch) params.search = effectiveSearch;
      if (statusFilter) params.status = statusFilter;
      if (returnStatusFilter) params.return_status = returnStatusFilter;
      if (sellerIdFilter) params.seller_id = sellerIdFilter;
      if (userIdFilter) params.user_id = userIdFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (
        transitionDateFrom &&
        transitionDateTo &&
        transitionTargetStatuses.length > 0
      ) {
        params.transition_to_statuses = transitionTargetStatuses.join(',');
        params.transition_date_from = transitionDateFrom;
        params.transition_date_to = transitionDateTo;
      }

      const response = await api.getAdminOrders(params);
      if (response.data) {
        setOrders(response.data);
        setTotalItems(response.total || 0);
        setTotalPages(response.total_pages || 0);
        
        // Get user info from response if filtering by user
        if (userIdFilter && response.user_info) {
          setFilterUserInfo(response.user_info);
        }
        return {
          data: response.data,
          total: Number(response.total) || 0,
        };
      }
      return { data: [], total: 0 };
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка загрузки заказов'));
      console.error('Failed to load orders:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    pageSize,
    searchQuery,
    statusFilter,
    returnStatusFilter,
    sellerIdFilter,
    userIdFilter,
    dateFrom,
    dateTo,
    transitionDateFrom,
    transitionDateTo,
    transitionTargetStatuses,
  ]);

  const mergeOrderItemsFromWbRefresh = useCallback((orderId, itemsPayload) => {
    const byId = {};
    (itemsPayload || []).forEach((row) => {
      byId[row.id] = row;
    });
    setOrders((prev) =>
      prev.map((o) => {
        if (o.id !== orderId) return o;
        const items = (o.items || []).map((it) => {
          const u = byId[it.id];
          if (!u) return it;
          const nextNm = u.wb_nm_id ?? it.wb_nm_id;
          return {
            ...it,
            wb_primary_image_url: u.wb_primary_image_url || it.wb_primary_image_url,
            wb_primary_image_big_url: u.wb_primary_image_big_url || it.wb_primary_image_big_url,
            wb_nm_id: nextNm,
            wb_product_url: it.wb_product_url || wbCatalogUrlFromNmId(nextNm),
          };
        });
        return { ...o, items };
      }),
    );
    setEditingOrder((prev) => {
      if (!prev || prev.id !== orderId) return prev;
      const items = (prev.items || []).map((it) => {
        const u = byId[it.id];
        if (!u) return it;
        const nextNm = u.wb_nm_id ?? it.wb_nm_id;
        return {
          ...it,
          wb_primary_image_url: u.wb_primary_image_url || it.wb_primary_image_url,
          wb_primary_image_big_url: u.wb_primary_image_big_url || it.wb_primary_image_big_url,
          wb_nm_id: nextNm,
          wb_product_url: it.wb_product_url || wbCatalogUrlFromNmId(nextNm),
        };
      });
      return { ...prev, items };
    });
  }, []);

  const handleRefreshWbImages = async (orderId) => {
    setWbRefreshingOrderId(orderId);
    setError('');
    try {
      const res = await api.postAdminOrderWbRefreshImages(orderId);
      if (res.items) mergeOrderItemsFromWbRefresh(orderId, res.items);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка обновления фото WB'));
    } finally {
      setWbRefreshingOrderId(null);
    }
  };

  const handleGenerateWhLabel = async (order) => {
    if (!order?.id) return;
    setLabelGeneratingOrderId(order.id);
    setError('');
    try {
      if (orderNeedsSkuBarcode(order)) {
        const preflight = await api.postAdminOrderSaleWbBarcodePreflight(order.id);
        if (preflightNeedsBarcodeModal(preflight)) {
          openSaleBarcodeModalFromPreflight(preflight, 'label', order);
          return;
        }
      }
      const res = await api.postAdminOrderGenerateWhLabel(order.id);
      const file = res?.data?.file;
      const filename = res?.data?.filename || `label-${order.platform_order_no || order.id}.pdf`;
      if (!file) {
        throw new Error('WH не вернул файл этикетки');
      }
      downloadBase64Pdf(file, filename);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось сгенерировать этикетку'));
    } finally {
      setLabelGeneratingOrderId(null);
    }
  };

  const seedSaleBarcodeSelections = useCallback((preflight) => {
    const selections = {};
    (preflight?.items || []).forEach((row) => {
      if (row.needs_selection) {
        const rec = (row.recommended_barcode || '').trim();
        const first = row.barcode_options?.[0]?.barcode || '';
        selections[row.id] = rec || first;
      } else if (row.needs_manual_barcode) {
        selections[row.id] = '';
      }
    });
    return selections;
  }, []);

  const openSaleBarcodeModalFromPreflight = useCallback((preflight, action, labelOrder = null) => {
    setSaleBarcodePreflight(preflight);
    setSaleBarcodeSelections(seedSaleBarcodeSelections(preflight));
    setSaleBarcodePreflightError('');
    setPendingSaleAction(action);
    setPendingLabelOrder(labelOrder);
    setShowSaleBarcodeModal(true);
  }, [seedSaleBarcodeSelections]);

  const runSaleBarcodePreflight = useCallback(async (orderId) => {
    setSaleBarcodePreflightLoading(true);
    setSaleBarcodePreflightError('');
    try {
      const preflight = await api.postAdminOrderSaleWbBarcodePreflight(orderId);
      setSaleBarcodePreflight(preflight);
      setSaleBarcodeSelections(seedSaleBarcodeSelections(preflight));
      return preflight;
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Ошибка загрузки штрихкодов WB');
      setSaleBarcodePreflightError(msg);
      throw err;
    } finally {
      setSaleBarcodePreflightLoading(false);
    }
  }, [seedSaleBarcodeSelections]);

  const saveSaleBarcodeSelectionsAndContinue = async () => {
    const orderId = pendingLabelOrder?.id || editingOrder?.id;
    if (!orderId || !saleBarcodePreflight) return;

    const rowsToSave = (saleBarcodePreflight.items || []).filter(
      (row) => row.needs_selection || row.needs_manual_barcode,
    );
    for (const row of rowsToSave) {
      const bc = (saleBarcodeSelections[row.id] || '').trim();
      if (!bc) {
        setSaleBarcodePreflightError('Выберите или введите штрихкод для каждой позиции');
        return;
      }
    }

    try {
      setSaleBarcodeSaving(true);
      setSaleBarcodePreflightError('');
      for (const row of rowsToSave) {
        const bc = (saleBarcodeSelections[row.id] || '').trim();
        await api.updateAdminOrder(orderId, {
          item_fields: { item_id: row.id, sku_barcode: bc },
        });
      }

      setShowSaleBarcodeModal(false);
      const action = pendingSaleAction;
      const labelOrder = pendingLabelOrder;
      setSaleBarcodePreflight(null);
      setSaleBarcodeSelections({});
      setPendingSaleAction(null);
      setPendingLabelOrder(null);

      if (action === 'label' && labelOrder) {
        setLabelGeneratingOrderId(labelOrder.id);
        try {
          const res = await api.postAdminOrderGenerateWhLabel(labelOrder.id);
          const file = res?.data?.file;
          const filename = res?.data?.filename || `label-${labelOrder.platform_order_no || labelOrder.id}.pdf`;
          if (!file) {
            throw new Error('WH не вернул файл этикетки');
          }
          downloadBase64Pdf(file, filename);
        } finally {
          setLabelGeneratingOrderId(null);
        }
        await loadOrders();
        return;
      }

      if (action === 'status_update' && editingOrder) {
        try {
          setSaving(true);
          setEditModalError('');
          setError('');
          await api.updateAdminOrder(editingOrder.id, editFormData);
          await loadOrders();
          setShowEditModal(false);
          setEditingOrder(null);
          setEditFormData({});
          setEditModalError('');
        } catch (err) {
          const msg = getApiErrorMessage(err, 'Не удалось сохранить заказ');
          setEditModalError(msg);
          setError(msg);
        } finally {
          setSaving(false);
        }
      }
    } catch (err) {
      setSaleBarcodePreflightError(getApiErrorMessage(err, 'Не удалось сохранить штрихкод'));
    } finally {
      setSaleBarcodeSaving(false);
    }
  };

  useEffect(() => {
    if (!user?.is_superuser) return;
    // В режиме приёмки поиск по полю (стикер/shk) — через handleSearch (авто-открытие редактирования)
    if (acceptanceMode && searchQuery.trim()) return;
    loadOrders();
  }, [loadOrders, user?.is_superuser, acceptanceMode, searchQuery]);

  useEffect(() => {
    if (!user?.is_superuser || !acceptanceMode || !searchQuery.trim()) return undefined;
    const timer = setTimeout(() => {
      handleSearchRef.current();
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery, acceptanceMode, user?.is_superuser]);
  
  // Helper function to add authentication token to attachment URLs
  const addTokenToAttachmentUrls = useCallback((order) => {
    if (!order || !order.items) return order;
    
    const token = api.getToken();
    if (!token) return order;
    
    // Deep clone the order to avoid mutating the original
    const processedOrder = JSON.parse(JSON.stringify(order));
    
    // Process each item's attachments
    if (processedOrder.items && Array.isArray(processedOrder.items)) {
      processedOrder.items = processedOrder.items.map(item => {
        if (item.attachments && Array.isArray(item.attachments)) {
          item.attachments = item.attachments.map(attachment => {
            if (attachment.file_url) {
              // Add token if not already present
              if (!attachment.file_url.includes('token=')) {
                const separator = attachment.file_url.includes('?') ? '&' : '?';
                attachment.file_url = `${attachment.file_url}${separator}token=${encodeURIComponent(token)}`;
              }
            }
            return attachment;
          });
        }
        return item;
      });
    }
    
    return processedOrder;
  }, []);

  /** Merge fresh attachments into open edit modal so new photos appear without closing it. */
  const syncEditingOrderItemsFromList = useCallback(
    (orderListOrResult) => {
      const orderList = Array.isArray(orderListOrResult)
        ? orderListOrResult
        : orderListOrResult?.data;
      if (!orderList || !Array.isArray(orderList)) return;
      setEditingOrder((prev) => {
        if (!prev?.id) return prev;
        const found = orderList.find((o) => o.id === prev.id);
        if (!found?.items?.length || !prev.items?.length) return prev;
        const processed = addTokenToAttachmentUrls(found);
        return {
          ...prev,
          items: prev.items.map((it, idx) =>
            idx === 0
              ? { ...it, attachments: processed.items[0]?.attachments ?? it.attachments }
              : it,
          ),
        };
      });
    },
    [addTokenToAttachmentUrls],
  );
  
  // WebSocket handlers for real-time updates
  const handleOrderUpdateFromWebSocket = useCallback((updatedOrder) => {
    console.log('Admin: Received order update via WebSocket:', updatedOrder);
    
    // Add authentication token to attachment URLs
    const processedOrder = addTokenToAttachmentUrls(updatedOrder);
    
    // Update order in the list if it exists
    setOrders(prevOrders => {
      const orderIndex = prevOrders.findIndex(order => order.id === processedOrder.id);
      
      if (orderIndex >= 0) {
        // Order is in current list, update it
        // Ensure we have all required fields from OrderDetailSerializer format
        const newOrders = [...prevOrders];
        newOrders[orderIndex] = {
          ...processedOrder,
          // Ensure items array exists and is properly formatted
          items: processedOrder.items || [],
        };
        console.log('Admin: Updated order in list:', newOrders[orderIndex]);
        return newOrders;
      } else {
        // Order is not in current list, refresh if we're on first page
        // (new orders typically appear on first page)
        if (currentPage === 1) {
          requestAnimationFrame(() => {
            loadOrders();
          });
        }
        return prevOrders;
      }
    });
  }, [currentPage, loadOrders, addTokenToAttachmentUrls]);
  
  const handleOrderCreated = useCallback((newOrder) => {
    // For new orders, refresh if we're on first page
    if (currentPage === 1) {
      requestAnimationFrame(() => {
        loadOrders();
      });
    }
  }, [currentPage, loadOrders]);
  
  // WebSocket push отключён: админ-таблица заказов не обновляется сама
  useOrdersWebSocket(
    user?.is_superuser ? handleOrderUpdateFromWebSocket : null,
    user?.is_superuser ? handleOrderCreated : null,
    false,
  );

  const toggleTransitionTargetStatus = (value) => {
    setCurrentPage(1);
    setTransitionTargetStatuses((prev) =>
      prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value],
    );
  };

  const clearTransitionFilter = () => {
    setCurrentPage(1);
    setTransitionDateFrom('');
    setTransitionDateTo('');
    setTransitionTargetStatuses([]);
  };

  const transitionFilterActive =
    Boolean(transitionDateFrom) &&
    Boolean(transitionDateTo) &&
    transitionTargetStatuses.length > 0;
  const transitionFilterPartial =
    Boolean(transitionDateFrom || transitionDateTo || transitionTargetStatuses.length > 0) &&
    !transitionFilterActive;

  // Handle order update (save changes). If transition from awaiting_receipt → show receipt modal first; if new status sent_to_seller → show prepare-return modal first.
  const handleOrderUpdate = async () => {
    if (!editingOrder) return;

    const allowedStatuses = getAllowedStatuses(editingOrder.status);
    if (editFormData.status !== editingOrder.status && !allowedStatuses.includes(editFormData.status)) {
      setEditModalError('This status change is not allowed');
      return;
    }

    if (editFormData.status === 'sent_to_seller' && !editFormData.seller_tracking_number) {
      setEditModalError('Seller tracking number is required when changing status to "Sent to seller"');
      return;
    }

    // Transition from awaiting_receipt to any other → show receipt modal (admin chooses warehouse or pickup and amount)
    if (editingOrder.status === 'awaiting_receipt' && editFormData.status !== 'awaiting_receipt') {
      setReceiptVariant('warehouse_default');
      setReceiptType('receipt_warehouse');
      setReceiptAmount('2');
      setShowReceiptModal(true);
      return;
    }

    // New status sent_to_seller → show prepare-return modal (confirm/edit 3.5 per item)
    if (editFormData.status === 'sent_to_seller') {
      setPrepareReturnAmount('3.5');
      setShowPrepareReturnModal(true);
      return;
    }

    // Передача в продажу: при пустом sku_barcode — выбор из WB Content API
    if (
      SALE_BARCODE_STATUSES.includes(editFormData.status)
      && orderNeedsSkuBarcode(editingOrder)
    ) {
      try {
        setSaving(true);
        const preflight = await runSaleBarcodePreflight(editingOrder.id);
        if (preflightNeedsBarcodeModal(preflight)) {
          setPendingSaleAction('status_update');
          setPendingLabelOrder(null);
          openSaleBarcodeModalFromPreflight(preflight, 'status_update', null);
          return;
        }
      } catch (err) {
        setEditModalError(getApiErrorMessage(err, 'Ошибка загрузки штрихкодов WB'));
        return;
      } finally {
        setSaving(false);
      }
    }

    try {
      setSaving(true);
      setEditModalError('');
      setError('');
      await api.updateAdminOrder(editingOrder.id, editFormData);
      await loadOrders();
      setShowEditModal(false);
      setEditingOrder(null);
      setEditFormData({});
      setEditModalError('');
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Не удалось сохранить заказ');
      setEditModalError(msg);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  /** Списание (billing) + смена статуса — только по данным в таблице; успех WB preflight не требуется. */
  const handleReceiptModalConfirm = async () => {
    if (!editingOrder) return;
    receiptPreflightAbortRef.current?.abort();
    if (!receiptMetricsFormValid(receiptMetricsById)) {
      setError('Укажите название товара, габариты Д×Ш×В (см) и вес (кг) по каждой позиции.');
      return;
    }
    let selectedType = 'receipt_warehouse';
    let selectedAmount = 2;
    if (receiptVariant === 'pickup_default') {
      selectedType = 'receipt_pickup';
      selectedAmount = 5.5;
    } else if (receiptVariant === 'custom') {
      const amount = parseFloat(receiptAmount);
      if (Number.isNaN(amount) || amount <= 0) {
        setError('Введите положительную сумму');
        return;
      }
      selectedType = receiptType;
      selectedAmount = amount;
    }
    const itemMetrics = Object.values(receiptMetricsById).map((r) => ({
      id: r.id,
      sku_name: (r.sku_name || '').trim(),
      sku_length: parseInt(r.sku_length, 10),
      sku_width: parseInt(r.sku_width, 10),
      sku_height: parseInt(r.sku_height, 10),
      sku_weight_kg: String(r.sku_weight_kg).replace(',', '.'),
    }));
    try {
      setReceiptSaving(true);
      setError('');
      await api.createBillingReceipt(editingOrder.id, selectedType, selectedAmount, {
        item_metrics: itemMetrics,
      });
      await api.updateAdminOrder(editingOrder.id, editFormData);
      await loadOrders();
      setShowReceiptModal(false);
      setShowEditModal(false);
      setEditingOrder(null);
      setEditFormData({});
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка создания приёмки'));
    } finally {
      setReceiptSaving(false);
    }
  };

  const handlePrepareReturnModalConfirm = async () => {
    if (!editingOrder) return;
    const amount = parseFloat(prepareReturnAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      setError('Введите положительную сумму');
      return;
    }
    try {
      setPrepareReturnSaving(true);
      setError('');
      await api.createBillingPrepareReturn(editingOrder.id, amount);
      await api.updateAdminOrder(editingOrder.id, editFormData);
      await loadOrders();
      setShowPrepareReturnModal(false);
      setShowEditModal(false);
      const orderId = editingOrder.id;
      const orderNo = editingOrder.platform_order_no || editingOrder.id;
      setEditingOrder(null);
      setEditFormData({});
      try {
        const res = await api.getBillingExpenses({ order_id: orderId, page_size: 50 });
        const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
        setBillingExpenses(list);
        setBillingOrderNo(orderNo);
        setShowBillingModal(true);
      } catch (e) {
        // non-blocking
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Ошибка создания списания'));
    } finally {
      setPrepareReturnSaving(false);
    }
  };

  const closeAttachmentModal = () => {
    setShowAttachmentModal(false);
    setAttachmentOrderItem(null);
    setAttachmentFiles([]);
    setAttachmentType('photo');
  };

  // Handle attachment upload (several photos at once, one video)
  const handleAttachmentUpload = async () => {
    if (!attachmentOrderItem || attachmentFiles.length === 0) return;

    let uploaded = 0;
    try {
      setUploadingAttachment(true);
      for (const file of attachmentFiles) {
        const formData = new FormData();
        formData.append('order_item', attachmentOrderItem.id);
        formData.append('attachment_type', attachmentType);
        formData.append('file', file);
        await api.requestFormData('/returns/attachments/', formData);
        uploaded += 1;
      }
      const orderList = await loadOrders();
      syncEditingOrderItemsFromList(orderList);
      closeAttachmentModal();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to upload attachment'));
      if (uploaded > 0) {
        const orderList = await loadOrders();
        syncEditingOrderItemsFromList(orderList);
      }
    } finally {
      setUploadingAttachment(false);
    }
  };

  // Handle attachment delete
  const handleAttachmentDelete = async (attachmentId) => {
    if (!window.confirm(t('adminOrders.deleteAttachment'))) return;
    
    try {
      await api.deleteReturnAttachment(attachmentId);
      const orderList = await loadOrders();
      syncEditingOrderItemsFromList(orderList);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to delete attachment'));
    }
  };

  const openBillingExpenseEdit = (expense) => {
    setEditingBillingExpense(expense);
    setBillingEditAmount(expense.amount != null ? String(expense.amount) : '');
    setBillingEditReason(expense.admin_override_reason || '');
    setBillingSaveError('');
  };

  const closeBillingExpenseEdit = () => {
    setEditingBillingExpense(null);
    setBillingSaveError('');
  };

  const handleBillingExpenseSave = async () => {
    if (!editingBillingExpense) return;
    const newAmount = parseFloat(billingEditAmount);
    if (Number.isNaN(newAmount)) {
      setBillingSaveError('Введите число');
      return;
    }
    const autoAmount = editingBillingExpense.auto_amount != null ? Number(editingBillingExpense.auto_amount) : null;
    if (autoAmount != null && newAmount !== autoAmount && !billingEditReason.trim()) {
      setBillingSaveError('При изменении суммы от авто-расчёта укажите причину.');
      return;
    }
    setBillingSaving(true);
    setBillingSaveError('');
    try {
      await api.updateBillingExpense(editingBillingExpense.id, {
        amount: newAmount,
        admin_override_reason: billingEditReason.trim() || undefined,
      });
      const res = await api.getBillingExpenses({ order_id: editingBillingExpense.order, page_size: 50 });
      const list = Array.isArray(res?.data) ? res.data : res?.data?.data ?? [];
      setBillingExpenses(list);
      closeBillingExpenseEdit();
    } catch (err) {
      const data = err.data || {};
      setBillingSaveError(data.admin_override_reason?.[0] || data.amount?.[0] || err.message || 'Ошибка сохранения');
    } finally {
      setBillingSaving(false);
    }
  };

  // Open edit modal
  const openEditModal = (order) => {
    setEditModalError('');
    setEditingOrder(order);
    const item = order.items?.[0];
    setEditFormData({
      status: order.status,
      customer_tracking_number: order.customer_tracking_number || '',
      tracking_number: order.tracking_number || '',
      seller_tracking_number: order.seller_tracking_number || '',
      seller_id: order.seller_id || '',
      item_fields: {
        is_defective: item?.is_defective,
        is_defect_after_check: item?.is_defect_after_check,
        inspection_comments: item?.inspection_comments || '',
      },
    });
    setShowEditModal(true);
  };

  const openEditModalFromOrderId = useCallback(async (orderId) => {
    try {
      const res = await api.getAdminOrder(orderId);
      if (res?.data) {
        openEditModal(addTokenToAttachmentUrls(res.data));
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Не удалось загрузить заказ'));
    }
  }, [addTokenToAttachmentUrls]);

  const tryAcceptanceAutoOpen = useCallback(
    async (result, { preferredOrderId = null, query = '' } = {}) => {
      if (!acceptanceMode) return;

      if (preferredOrderId) {
        const fromList = result?.data?.find((o) => o.id === preferredOrderId);
        if (fromList) {
          openEditModal(addTokenToAttachmentUrls(fromList));
          return;
        }
        await openEditModalFromOrderId(preferredOrderId);
        return;
      }

      const q = String(query || '').trim();
      const total = Number(result?.total) || 0;
      if (q && result?.data?.length === 1 && total === 1) {
        openEditModal(addTokenToAttachmentUrls(result.data[0]));
      }
    },
    [acceptanceMode, addTokenToAttachmentUrls, openEditModalFromOrderId],
  );

  const handleAcceptanceModeChange = (enabled) => {
    setAcceptanceMode(enabled);
    try {
      localStorage.setItem(ACCEPTANCE_MODE_STORAGE_KEY, String(enabled));
    } catch {
      /* ignore */
    }
  };

  const handleSearch = useCallback(async () => {
    const q = searchQuery.trim();
    setCurrentPage(1);
    setError('');
    const result = await loadOrders({ page: 1, search: q });
    await tryAcceptanceAutoOpen(result, { query: q });
  }, [searchQuery, loadOrders, tryAcceptanceAutoOpen]);

  useEffect(() => {
    handleSearchRef.current = handleSearch;
  }, [handleSearch]);

  // When opened from "Find return by shkId" with initialEditOrderId, fetch order and open edit modal
  useEffect(() => {
    if (!initialEditOrderId || !onClearInitialEditOrderId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.getAdminOrder(initialEditOrderId);
        if (cancelled) return;
        if (res?.data) {
          openEditModal(res.data);
        }
      } catch (err) {
        if (!cancelled) setError(getApiErrorMessage(err, 'Failed to load order'));
      } finally {
        if (!cancelled) onClearInitialEditOrderId();
      }
    })();
    return () => { cancelled = true; };
  }, [initialEditOrderId, onClearInitialEditOrderId]);

  // Check if user is superuser
  if (!user?.is_superuser) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          {t('adminOrders.accessDenied')}
        </Alert>
      </Container>
    );
  }

  // Clear user filter
  const clearUserFilter = () => {
    setUserIdFilter(null);
    setFilterUserInfo(null);
    // Update URL to remove user_id param
    window.history.replaceState({}, document.title, window.location.pathname);
    setCurrentPage(1);
  };

  return (
    <Container className="py-4 admin-orders-page" fluid>
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-bottom">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h4 className="mb-0 fw-bold" style={{ color: 'var(--bs-body-color)' }}>
                {userIdFilter ? 'Заказы пользователя' : 'Управление заказами'}
              </h4>
              {userIdFilter && (
                <small className="text-muted">
                  {filterUserInfo ? (
                    <>
                      <i className="bi bi-person me-1"></i>
                      {filterUserInfo.full_name} ({filterUserInfo.email}) • Найдено заказов: {totalItems}
                    </>
                  ) : (
                    <>ID пользователя: {userIdFilter} • Найдено заказов: {totalItems}</>
                  )}
                </small>
              )}
            </div>
            {userIdFilter && (
              <Button variant="outline-secondary" size="sm" onClick={clearUserFilter}>
                <i className="bi bi-x-lg me-1"></i>
                Показать все заказы
              </Button>
            )}
          </div>
        </Card.Header>
        <Card.Body>
          {pageErrorText && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {pageErrorText}
            </Alert>
          )}

          {/* Filters */}
          <Row className="mb-3 g-3">
            <Col xs={12} sm={12} md={6} lg={4}>
              <InputGroup>
                <Form.Control
                  type="text"
                  placeholder={t('adminOrders.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <Button variant="outline-secondary" onClick={handleSearch}>
                  <i className="bi bi-search"></i>
                </Button>
              </InputGroup>
            </Col>
            <Col xs={6} sm={6} md={3} lg={2}>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="">{t('adminOrders.allStatuses')}</option>
                {statusChoices.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.display}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} sm={6} md={3} lg={2}>
              <Form.Select
                value={returnStatusFilter}
                onChange={(e) => setReturnStatusFilter(e.target.value)}
              >
                <option value="">{t('adminOrders.allReturnStatuses')}</option>
                <option value="1">{t('adminOrders.returnStatusActive')}</option>
                <option value="0">{t('adminOrders.returnStatusArchived')}</option>
              </Form.Select>
            </Col>
            <Col xs={6} sm={6} md={3} lg={2}>
                <Form.Control
                  type="text"
                  placeholder={t('adminOrders.storeIdPlaceholder')}
                  value={sellerIdFilter}
                  onChange={(e) => setSellerIdFilter(e.target.value)}
                />
            </Col>
            <Col xs={6} sm={6} md={6} lg={2}>
              <Form.Control
                type="date"
                placeholder="От"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </Col>
            <Col xs={6} sm={6} md={6} lg={2}>
              <Form.Control
                type="date"
                placeholder="До"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </Col>
            <Col xs={12} sm="auto" className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="admin-orders-acceptance-mode"
                checked={acceptanceMode}
                onChange={(e) => handleAcceptanceModeChange(e.target.checked)}
                label={t('adminOrders.acceptanceMode')}
                className="mb-0 user-select-none"
              />
            </Col>
          </Row>

          <Row className="mb-3 g-3 align-items-start">
            <Col xs={12}>
              <div className="fw-semibold small mb-1">{t('adminOrders.statusTransitionSection')}</div>
              <div className="text-muted small mb-2">{t('adminOrders.statusTransitionHint')}</div>
            </Col>
            <Col xs={6} sm={4} md={3} lg={2}>
              <Form.Label className="small text-muted mb-1">{t('adminOrders.statusTransitionDateFrom')}</Form.Label>
              <Form.Control
                type="date"
                value={transitionDateFrom}
                onChange={(e) => {
                  setCurrentPage(1);
                  setTransitionDateFrom(e.target.value);
                }}
              />
            </Col>
            <Col xs={6} sm={4} md={3} lg={2}>
              <Form.Label className="small text-muted mb-1">{t('adminOrders.statusTransitionDateTo')}</Form.Label>
              <Form.Control
                type="date"
                value={transitionDateTo}
                onChange={(e) => {
                  setCurrentPage(1);
                  setTransitionDateTo(e.target.value);
                }}
              />
            </Col>
            <Col xs={12} sm={4} md="auto" className="d-flex align-items-end">
              <Button variant="outline-secondary" size="sm" type="button" onClick={clearTransitionFilter}>
                {t('adminOrders.statusTransitionClear')}
              </Button>
            </Col>
            <Col xs={12}>
              <Form.Label className="small text-muted mb-1">{t('adminOrders.statusTransitionTargetStatuses')}</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {statusChoices.map((s) => (
                  <Form.Check
                    key={s.value}
                    type="checkbox"
                    id={`admin-tr-st-${s.value}`}
                    checked={transitionTargetStatuses.includes(s.value)}
                    onChange={() => toggleTransitionTargetStatus(s.value)}
                    label={s.display}
                    className="small"
                  />
                ))}
              </div>
            </Col>
            {transitionFilterPartial && (
              <Col xs={12}>
                <Alert variant="light" className="py-2 px-3 mb-0 small">
                  {t('adminOrders.statusTransitionIncomplete')}
                </Alert>
              </Col>
            )}
            {transitionFilterActive && (
              <Col xs={12}>
                <Alert variant="info" className="py-2 px-3 mb-0 small">
                  {t('adminOrders.statusTransitionActiveNote', { total: totalItems })}
                </Alert>
              </Col>
            )}
          </Row>

          {/* Orders Table */}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <div className="table-wrapper">
                <Table hover>
                  <thead>
                    <tr>
                      <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.orderNumber')}</th>
                      <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.sticker')}</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)', width: 88 }}>{t('adminOrders.wbProductPhoto')}</th>
                      <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.status')}</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.store')}</th>
                      <th className="d-none d-lg-table-cell" style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.trackingNumbers')}</th>
                      <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.product')}</th>
                      <th className="d-none d-lg-table-cell" style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.dstOfficeAddress')}</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.returnStatus')}</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.readyToReturnDt')}</th>
                      <th className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.createdDate')}</th>
                      <th style={{ color: 'var(--bs-body-color)', backgroundColor: 'var(--bs-table-bg)' }}>{t('adminOrders.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const item = order.items?.[0];
                      const isExpanded = expandedRow === order.id;
                      const storeHighlight = isOrderRowHighlighted(order);
                      return (
                        <React.Fragment key={order.id}>
                          <tr
                              className={[
                                order.from_returns_import ? 'from-returns-import-row' : '',
                                storeHighlight ? 'admin-highlight-store-order' : '',
                              ].filter(Boolean).join(' ')}
                              style={order.from_returns_import ? { backgroundColor: 'rgba(255, 243, 205, 0.5)' } : undefined}
                            >
                            <td style={{ color: 'var(--bs-body-color)' }}>
                              <div className="d-flex flex-column align-items-start gap-1">
                                <span>{order.platform_order_no}</span>
                                {order.status === 'submitted_for_sale' && (
                                  <Button
                                    variant="outline-primary"
                                    className="text-nowrap mt-1"
                                    disabled={labelGeneratingOrderId === order.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleGenerateWhLabel(order);
                                    }}
                                  >
                                    {labelGeneratingOrderId === order.id ? (
                                      <>
                                        <Spinner animation="border" size="sm" className="me-2" />
                                        Генерация…
                                      </>
                                    ) : (
                                      'Сгенерировать этикетку'
                                    )}
                                  </Button>
                                )}
                              </div>
                            </td>
                            <td style={{ color: 'var(--bs-body-color)' }}>{order.sticker_id || '-'}</td>
                            <td className="d-none d-md-table-cell align-middle">
                              <div className="d-flex flex-column align-items-center gap-1">
                                {item?.wb_primary_image_url ? (
                                  <button
                                    type="button"
                                    className="p-0 border-0 bg-transparent lh-1"
                                    title={t('returns.wbPhotoFullscreen')}
                                    aria-label={t('returns.wbPhotoFullscreen')}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openWbProductLightbox(
                                        item.wb_primary_image_big_url || item.wb_primary_image_url,
                                      );
                                    }}
                                  >
                                    <img
                                      src={item.wb_primary_image_url}
                                      alt=""
                                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, cursor: 'pointer', display: 'block' }}
                                      key={`${order.id}-nm${item.wb_nm_id ?? 'x'}-img`}
                                    />
                                  </button>
                                ) : adminWbThumbNotFoundByItemId[item?.id] ? (
                                  <span className="text-muted small">{t('returns.wbImageNotFound')}</span>
                                ) : (
                                  <span className="text-muted small">—</span>
                                )}
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="py-0 px-1"
                                  title={t('adminOrders.refreshWbPhotoTooltip')}
                                  disabled={wbRefreshingOrderId === order.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRefreshWbImages(order.id);
                                  }}
                                >
                                  {wbRefreshingOrderId === order.id ? (
                                    <Spinner animation="border" size="sm" />
                                  ) : (
                                    <i className="bi bi-arrow-clockwise" />
                                  )}
                                </Button>
                              </div>
                            </td>
                            <td>
                              {['received_awaiting_check', 'awaiting_for_sale', 'transferring_to_sale', 'awaiting_shipment', 'awaiting_disposal'].includes(order.status) ? (
                                <span
                                  onClick={() => openEditModal(order)}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <Badge 
                                    {...getStatusBadgeProps(order.status)}
                                    style={{ 
                                      fontWeight: 'bold',
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '0.8'}
                                    onMouseLeave={(e) => e.target.style.opacity = '1'}
                                  >
                                    {getStatusLabel(order.status)}
                                  </Badge>
                                </span>
                              ) : (
                                <Badge {...getStatusBadgeProps(order.status)}>
                                  {getStatusLabel(order.status)}
                                </Badge>
                              )}
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>
                              <StoreSellerCell order={order} />
                            </td>
                            <td className="d-none d-lg-table-cell">
                              <small style={{ color: 'var(--bs-body-color)' }}>
                                {order.customer_tracking_number && (
                                  <div>{t('adminOrders.customer')}: {order.customer_tracking_number}</div>
                                )}
                                {order.tracking_number && (
                                  <div>{t('adminOrders.tracking')}: {order.tracking_number}</div>
                                )}
                                {order.seller_tracking_number && (
                                  <div>{t('adminOrders.seller')}: {order.seller_tracking_number}</div>
                                )}
                              </small>
                            </td>
                            <td style={{ color: 'var(--bs-body-color)' }}>
                              {item && (
                                <div>
                                  {/* Show translated name if available, otherwise original name */}
                                  {item.sku_name_translated ? (
                                    <>
                                      <div style={{ color: 'var(--bs-body-color)', fontWeight: 'bold' }}>
                                        {item.sku_name_translated}
                                      </div>
                                      <div style={{ color: 'var(--bs-body-color)', opacity: 0.7, fontSize: '0.85em', fontStyle: 'italic' }}>
                                        {item.sku_name || item.sku_code}
                                      </div>
                                    </>
                                  ) : (
                                    <div style={{ color: 'var(--bs-body-color)' }}>
                                      {item.sku_name || item.sku_code}
                                    </div>
                                  )}
                                  <small style={{ color: 'var(--bs-body-color)', opacity: 0.8 }}>SKU: {item.sku_code}</small>
                                  {(wbAdminCatalogUrl(item) ||
                                    (user?.is_superuser &&
                                      (order.wh_portal_products_url || '').trim())) && (
                                    <div className="small mt-1 d-flex flex-column align-items-start gap-1">
                                      {wbAdminCatalogUrl(item) && (
                                        <a
                                          href={wbAdminCatalogUrl(item)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: 'var(--bs-link-color)' }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {t('returns.wbOpenOnWildberries')}
                                          <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.85em' }} aria-hidden />
                                        </a>
                                      )}
                                      {user?.is_superuser && (order.wh_portal_products_url || '').trim() && (
                                        <a
                                          href={(order.wh_portal_products_url || '').trim()}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: 'var(--bs-link-color)' }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {t('returns.whPortalProducts')}
                                          <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.85em' }} aria-hidden />
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  <div className="small text-muted mt-1">{formatAdminItemDimsWeight(item, t)}</div>
                                </div>
                              )}
                            </td>
                            <td className="d-none d-lg-table-cell" style={{ color: 'var(--bs-body-color)', maxWidth: 180 }} title={order.dst_office_address || ''}>
                              {order.dst_office_address ? (order.dst_office_address.length > 40 ? `${order.dst_office_address.slice(0, 40)}…` : order.dst_office_address) : '-'}
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>{order.is_status_active_display || '-'}</td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>
                              {order.ready_to_return_dt ? new Date(order.ready_to_return_dt).toLocaleString() : '-'}
                            </td>
                            <td className="d-none d-md-table-cell" style={{ color: 'var(--bs-body-color)' }}>{new Date(order.created_at).toLocaleDateString()}</td>
                            <td>
                              <div className="d-flex gap-1 flex-wrap">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => setExpandedRow(isExpanded ? null : order.id)}
                                >
                                  {isExpanded ? t('adminOrders.hide') : t('adminOrders.details')}
                                </Button>
                                <Button
                                  variant="outline-success"
                                  size="sm"
                                  onClick={() => openEditModal(order)}
                                >
                                  {t('adminOrders.edit')}
                                </Button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="12" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--bs-body-color)' }}>
                                <Row className="p-3">
                                  <Col xs={12} md={6}>
                                    <h6 style={{ color: 'var(--bs-body-color)' }}>{t('adminOrders.orderInfo')}</h6>
                                    <div className="d-lg-none mt-2 mb-2">
                                      <p style={{ color: 'var(--bs-body-color)' }}>
                                        <strong>{t('adminOrders.store')}:</strong>{' '}
                                        <StoreSellerCell order={order} />
                                      </p>
                                      <p style={{ color: 'var(--bs-body-color)' }}>
                                        <strong>{t('adminOrders.trackingNumbers')}:</strong>
                                        {order.customer_tracking_number && (
                                          <div>{t('adminOrders.customer')}: {order.customer_tracking_number}</div>
                                        )}
                                        {order.tracking_number && (
                                          <div>{t('adminOrders.tracking')}: {order.tracking_number}</div>
                                        )}
                                        {order.seller_tracking_number && (
                                          <div>{t('adminOrders.seller')}: {order.seller_tracking_number}</div>
                                        )}
                                      </p>
                                      <p style={{ color: 'var(--bs-body-color)' }}><strong>{t('adminOrders.createdDate')}:</strong> {new Date(order.created_at).toLocaleDateString()}</p>
                                    </div>
                                    <p style={{ color: 'var(--bs-body-color)' }} className="mt-2">
                                      <strong>{t('adminOrders.dstOfficeAddress')}:</strong>{' '}
                                      {order.dst_office_address || '-'}
                                    </p>
                                    {order.goods_return_reason && (
                                      <p style={{ color: 'var(--bs-body-color)' }} className="mt-2 mb-1">
                                        <strong>{t('adminOrders.goodsReturnReason')}:</strong>{' '}
                                        {order.goods_return_reason}
                                      </p>
                                    )}
                                    {item && (
                                      <>
                                        <p style={{ color: 'var(--bs-body-color)' }}><strong>{t('adminOrders.defective')}:</strong> {item.is_defective !== null ? (item.is_defective ? t('common.yes') : t('common.no')) : '-'}</p>
                                        <p style={{ color: 'var(--bs-body-color)' }}><strong>{t('adminOrders.defectAfterCheck')}:</strong> {item.is_defect_after_check !== null ? (item.is_defect_after_check ? t('common.yes') : t('common.no')) : '-'}</p>
                                        <p style={{ color: 'var(--bs-body-color)' }}><strong>{t('adminOrders.comments')}:</strong> {item.inspection_comments || '-'}</p>
                                        <p style={{ color: 'var(--bs-body-color)' }} className="mb-0">
                                          <strong>{t('adminOrders.dimensionsWeight')}:</strong>{' '}
                                          {formatAdminItemDimsWeight(item, t)}
                                        </p>
                                        {wbAdminCatalogUrl(item) ||
                                        (user?.is_superuser &&
                                          (order.wh_portal_products_url || '').trim()) ? (
                                          <p style={{ color: 'var(--bs-body-color)' }} className="mb-0 mt-2">
                                            <strong>{t('returns.wbCatalogShort')}:</strong>{' '}
                                            {wbAdminCatalogUrl(item) && (
                                              <a
                                                href={wbAdminCatalogUrl(item)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                              >
                                                {t('returns.wbOpenOnWildberries')}
                                                <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.85em' }} aria-hidden />
                                              </a>
                                            )}
                                            {user?.is_superuser &&
                                              (order.wh_portal_products_url || '').trim() && (
                                                <>
                                                  {wbAdminCatalogUrl(item) ? ' · ' : null}
                                                  <a
                                                    href={(order.wh_portal_products_url || '').trim()}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    {t('returns.whPortalProducts')}
                                                    <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.85em' }} aria-hidden />
                                                  </a>
                                                </>
                                              )}
                                          </p>
                                        ) : null}
                                      </>
                                    )}
                                  </Col>
                                  <Col xs={12} md={6}>
                                    {item && item.attachments && item.attachments.length > 0 && (
                                      <>
                                        <h6 style={{ color: 'var(--bs-body-color)' }}>{t('adminOrders.attachments')}</h6>
                                        <div className="d-flex flex-wrap" style={{ gap: '1rem' }}>
                                          {item.attachments.map((attachment, index) => {
                                            const photoAttachments = item.attachments.filter(a => a.attachment_type === 'photo');
                                            const photoIndex = photoAttachments.findIndex(a => a.id === attachment.id);
                                            
                                            return (
                                              <div key={attachment.id} className="position-relative" style={{ marginBottom: '0.5rem' }}>
                                                {attachment.attachment_type === 'photo' ? (
                                                  <img
                                                    src={attachment.file_url}
                                                    alt={attachment.file_name}
                                                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', display: 'block' }}
                                                    onClick={() => {
                                                      setLightboxPhotos(photoAttachments);
                                                      setLightboxIndex(photoIndex >= 0 ? photoIndex : 0);
                                                      setLightboxPhoto(photoAttachments[photoIndex >= 0 ? photoIndex : 0]);
                                                    }}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(e) => {
                                                      if (e.key === 'Enter') {
                                                        setLightboxPhotos(photoAttachments);
                                                        setLightboxIndex(photoIndex >= 0 ? photoIndex : 0);
                                                        setLightboxPhoto(photoAttachments[photoIndex >= 0 ? photoIndex : 0]);
                                                      }
                                                    }}
                                                  />
                                                ) : (
                                                  <video
                                                    src={attachment.file_url}
                                                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: '4px', display: 'block' }}
                                                    controls
                                                  />
                                                )}
                                                <Button
                                                  variant="danger"
                                                  size="sm"
                                                  className="position-absolute top-0 end-0"
                                                  style={{ transform: 'translate(50%, -50%)', zIndex: 10 }}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAttachmentDelete(attachment.id);
                                                  }}
                                                >
                                                  <i className="bi bi-x"></i>
                                                </Button>
                                              </div>
                                            );
                                          })}
                                        </div>
                                        <Button
                                          variant="outline-primary"
                                          size="sm"
                                          className="mt-2"
                                          onClick={() => {
                                            setAttachmentOrderItem(item);
                                            setShowAttachmentModal(true);
                                          }}
                                        >
                                          {t('adminOrders.addAttachment')}
                                        </Button>
                                      </>
                                    )}
                                    {item && (!item.attachments || item.attachments.length === 0) && (
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                          setAttachmentOrderItem(item);
                                          setShowAttachmentModal(true);
                                        }}
                                      >
                                        {t('adminOrders.addAttachment')}
                                      </Button>
                                    )}
                                  </Col>
                                </Row>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div style={{ color: 'var(--bs-body-color)' }}>
                    {t('adminOrders.showing', { from: ((currentPage - 1) * pageSize) + 1, to: Math.min(currentPage * pageSize, totalItems), total: totalItems })}
                  </div>
                  <Pagination>
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    />
                    {[...Array(totalPages)].map((_, i) => {
                      const page = i + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <Pagination.Item
                            key={page}
                            active={page === currentPage}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </Pagination.Item>
                        );
                      } else if (
                        page === currentPage - 3 ||
                        page === currentPage + 3
                      ) {
                        return <Pagination.Ellipsis key={page} />;
                      }
                      return null;
                    })}
                    <Pagination.Next
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      {/* Edit Modal */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditModalError('');
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: 'var(--bs-body-color)' }}>{t('adminOrders.editOrder')} {editingOrder?.platform_order_no}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editModalError && (
            <Alert variant="danger" dismissible onClose={() => setEditModalError('')} className="mb-3">
              {editModalError}
            </Alert>
          )}
          {editingOrder && (
            <Form>
              {editingOrder.goods_return_reason && (
                <Alert variant="light" className="py-2 small mb-3 border">
                  <strong>{t('adminOrders.goodsReturnReason')}:</strong>{' '}
                  {editingOrder.goods_return_reason}
                </Alert>
              )}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('adminOrders.statusLabel')}</Form.Label>
                    <Form.Select
                      value={editFormData.status || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    >
                      <option value={editingOrder.status}>{getStatusLabel(editingOrder.status)} (текущий)</option>
                      {statusChoices
                        .filter(s => getAllowedStatuses(editingOrder.status).includes(s.value))
                        .map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.display}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('adminOrders.storeId')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={editFormData.seller_id || ''}
                      readOnly
                      disabled
                      style={{ backgroundColor: 'var(--bs-secondary-bg)', cursor: 'not-allowed' }}
                    />
                    {editingOrder?.store_missing_in_system && (
                      <div className="fw-bold text-danger small mt-1">
                        {STORE_MISSING_REMARK}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('adminOrders.customerTrackingNumber')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={editFormData.customer_tracking_number || ''}
                      readOnly
                      disabled
                      style={{ backgroundColor: 'var(--bs-secondary-bg)', cursor: 'not-allowed' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('adminOrders.trackingNumber')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={editFormData.tracking_number || ''}
                      readOnly
                      disabled
                      style={{ backgroundColor: 'var(--bs-secondary-bg)', cursor: 'not-allowed' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t('adminOrders.sellerTrackingNumber')}
                      {editFormData.status === 'sent_to_seller' && <span className="text-danger"> *</span>}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editFormData.seller_tracking_number || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, seller_tracking_number: e.target.value })}
                      required={editFormData.status === 'sent_to_seller'}
                    />
                  </Form.Group>
                </Col>
              </Row>
              {editFormData.item_fields && editingOrder?.items?.length > 0 && (
                <>
                  <hr />
                  <h6 style={{ color: 'var(--bs-body-color)' }}>{t('adminOrders.dimensionsWeight')}</h6>
                  {editingOrder.items.map((it) => (
                    <p key={it.id} className="small text-muted mb-1">
                      {editingOrder.items.length > 1 && (
                        <span className="text-body fw-semibold me-1">ID {it.id}:</span>
                      )}
                      {formatAdminItemDimsWeight(it, t)}
                    </p>
                  ))}
                </>
              )}
                  {editFormData.item_fields && editingOrder?.items?.[0] && (
                <>
                  <hr />
                  <h6 style={{ color: 'var(--bs-body-color)' }}>{t('adminOrders.itemFields')}</h6>
                  {editingOrder.items[0].wb_primary_image_url ? (
                    <div className="mb-3">
                      <Form.Label className="small text-muted">{t('adminOrders.wbProductPhoto')}</Form.Label>
                      <div>
                        <button
                          type="button"
                          className="p-0 border-0 bg-transparent lh-1"
                          title={t('returns.wbPhotoFullscreen')}
                          aria-label={t('returns.wbPhotoFullscreen')}
                          onClick={() =>
                            openWbProductLightbox(
                              editingOrder.items[0].wb_primary_image_big_url ||
                                editingOrder.items[0].wb_primary_image_url,
                            )
                          }
                        >
                          <img
                            src={editingOrder.items[0].wb_primary_image_url}
                            alt=""
                            style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 6, cursor: 'pointer', display: 'block' }}
                          />
                        </button>
                      </div>
                    </div>
                  ) : adminWbThumbNotFoundByItemId[editingOrder.items[0].id] ? (
                    <div className="mb-3">
                      <Form.Label className="small text-muted">{t('adminOrders.wbProductPhoto')}</Form.Label>
                      <div className="text-muted small">{t('returns.wbImageNotFound')}</div>
                    </div>
                  ) : null}
                  {(wbAdminCatalogUrl(editingOrder.items[0]) ||
                    (user?.is_superuser &&
                      (editingOrder.wh_portal_products_url || '').trim())) && (
                    <div className="mb-3 d-flex flex-column align-items-start gap-1">
                      {wbAdminCatalogUrl(editingOrder.items[0]) && (
                        <a
                          href={wbAdminCatalogUrl(editingOrder.items[0])}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('returns.wbOpenOnWildberries')}
                          <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.85em' }} aria-hidden />
                        </a>
                      )}
                      {user?.is_superuser && (editingOrder.wh_portal_products_url || '').trim() && (
                        <a
                          href={(editingOrder.wh_portal_products_url || '').trim()}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {t('returns.whPortalProducts')}
                          <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.85em' }} aria-hidden />
                        </a>
                      )}
                    </div>
                  )}
                  {/* Attachment upload section in edit modal */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label className="d-block mb-2">{t('adminOrders.attachments')}</Form.Label>
                        {editingOrder.items[0].attachments && editingOrder.items[0].attachments.length > 0 && (
                          <div className="d-flex flex-wrap mb-3" style={{ gap: '1rem' }}>
                            {editingOrder.items[0].attachments.map((attachment) => {
                              const photoAttachments = editingOrder.items[0].attachments.filter(a => a.attachment_type === 'photo');
                              const photoIndex = photoAttachments.findIndex(a => a.id === attachment.id);
                              
                              return (
                                <div key={attachment.id} className="position-relative" style={{ marginBottom: '0.5rem' }}>
                                  {attachment.attachment_type === 'photo' ? (
                                    <img
                                      src={attachment.file_url}
                                      alt={attachment.file_name}
                                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: '4px', cursor: 'pointer', display: 'block' }}
                                      onClick={() => {
                                        setLightboxPhotos(photoAttachments);
                                        setLightboxIndex(photoIndex >= 0 ? photoIndex : 0);
                                        setLightboxPhoto(photoAttachments[photoIndex >= 0 ? photoIndex : 0]);
                                      }}
                                      role="button"
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          setLightboxPhotos(photoAttachments);
                                          setLightboxIndex(photoIndex >= 0 ? photoIndex : 0);
                                          setLightboxPhoto(photoAttachments[photoIndex >= 0 ? photoIndex : 0]);
                                        }
                                      }}
                                    />
                                  ) : (
                                    <video
                                      src={attachment.file_url}
                                      style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: '4px', display: 'block' }}
                                      controls
                                    />
                                  )}
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="position-absolute top-0 end-0"
                                    style={{ transform: 'translate(50%, -50%)', zIndex: 10 }}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      if (window.confirm(t('adminOrders.deleteAttachment'))) {
                                        await handleAttachmentDelete(attachment.id);
                                      }
                                    }}
                                  >
                                    <i className="bi bi-x"></i>
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="mt-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setAttachmentOrderItem(editingOrder.items[0]);
                              setShowAttachmentModal(true);
                            }}
                          >
                            <i className="bi bi-plus-lg me-1"></i>
                            {t('adminOrders.addAttachment')}
                          </Button>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label={t('adminOrders.defective')}
                          checked={editFormData.item_fields.is_defective === true}
                          disabled
                          readOnly
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          label={t('adminOrders.defectAfterCheck')}
                          checked={editFormData.item_fields.is_defect_after_check === true}
                          onChange={(e) => setEditFormData({
                            ...editFormData,
                            item_fields: {
                              ...editFormData.item_fields,
                              is_defect_after_check: e.target.checked ? true : null,
                            },
                          })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>{t('adminOrders.inspectionComments')}</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editFormData.item_fields.inspection_comments || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        item_fields: {
                          ...editFormData.item_fields,
                          inspection_comments: e.target.value,
                        },
                      })}
                    />
                  </Form.Group>
                </>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowEditModal(false);
              setEditModalError('');
            }}
          >
            {t('adminOrders.cancel')}
          </Button>
          <Button variant="primary" onClick={handleOrderUpdate} disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : t('adminOrders.save')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Receipt modal: when transitioning from awaiting_receipt — choose warehouse or pickup and amount */}
      <Modal
        show={showReceiptModal}
        onHide={() => {
          receiptPreflightAbortRef.current?.abort();
          receiptPreflightAbortRef.current = null;
          setShowReceiptModal(false);
          setReceiptPreflightError('');
          setReceiptGoodsReturnSync(null);
          setReceiptMetricsById({});
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>Приёмка возврата</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            При переводе из «Ожидает получения» выберите один из 3 вариантов списания за позицию.
            Данные товара подтягиваются из WB; если карточка не найдена — введите название, габариты (см) и вес (кг) вручную.
            {' '}
            <strong>Создать списание и сменить статус</strong> доступно, когда таблица заполнена корректно — сбой
            фоновой синхронизации с WB не блокирует сохранение.
          </p>
          {receiptPreflightLoading && (
            <Alert variant="light" border="secondary" className="py-2 small mb-3">
              <div className="d-flex align-items-start gap-2">
                <Spinner animation="border" size="sm" className="mt-1 flex-shrink-0" />
                <div>
                  <strong>Синхронизация с Wildberries…</strong>
                  <div className="text-muted mt-1">
                    Поля ниже уже доступны: можно ввести название, габариты (см) и вес (кг) вручную.
                    Когда ответ WB придёт, пустые поля заполнятся автоматически (уже введённые не перезаписываются).
                  </div>
                </div>
              </div>
            </Alert>
          )}
          {receiptPreflightError && (
            <Alert
              variant={receiptMetricsFormValid(receiptMetricsById) ? 'info' : 'warning'}
              className="py-2 small mb-3"
            >
              {receiptMetricsFormValid(receiptMetricsById) ? (
                <>
                  <strong>Не удалось подтянуть данные из Wildberries</strong>
                  {' '}
                  <span className="text-muted">({receiptPreflightError}).</span>
                  {' '}
                  Таблица ниже уже заполнена из заказа — это не ошибка формы; при необходимости
                  поправьте значения и сохраните приёмку.
                </>
              ) : (
                <>
                  {receiptPreflightError}
                  {' '}
                  Заполните поля ниже вручную.
                </>
              )}
            </Alert>
          )}
          {receiptGoodsReturnSync === false && (
            <Alert variant="info" className="py-2 small mb-3">
              Полная синхронизация возврата WB не удалась (нет совпадения в выгрузке или ошибка API).
              Габариты могли подтянуться по nmId из каталога; при необходимости исправьте вручную.
            </Alert>
          )}
          {editingOrder?.goods_return_reason && (
            <Alert variant="secondary" className="py-2 small mb-3">
              <strong>{t('adminOrders.goodsReturnReason')}:</strong>{' '}
              {editingOrder.goods_return_reason}
            </Alert>
          )}
          {Object.keys(receiptMetricsById).length > 0 && (
            <div className="mb-4">
              <h6 className="mb-2">Позиции заказа</h6>
              <Table size="sm" bordered responsive className="align-middle">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>nm</th>
                    <th>Название</th>
                    <th>Д×Ш×В (см)</th>
                    <th>Вес (кг)</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {(editingOrder?.items || [])
                    .map((it) => receiptMetricsById[it.id])
                    .filter(Boolean)
                    .map((row) => (
                      <tr key={row.id}>
                        <td className="text-muted small">{row.id}</td>
                        <td className="small">{row.nm_id ?? '—'}</td>
                        <td style={{ minWidth: 140 }}>
                          <Form.Control
                            size="sm"
                            value={row.sku_name}
                            onChange={(e) => setReceiptMetricsById((prev) => ({
                              ...prev,
                              [row.id]: { ...prev[row.id], sku_name: e.target.value },
                            }))}
                            placeholder="Название"
                          />
                        </td>
                        <td>
                          <div className="d-flex gap-1 flex-wrap">
                            <Form.Control
                              size="sm"
                              type="number"
                              min={1}
                              step={1}
                              style={{ width: 64 }}
                              value={row.sku_length}
                              onChange={(e) => setReceiptMetricsById((prev) => ({
                                ...prev,
                                [row.id]: { ...prev[row.id], sku_length: e.target.value },
                              }))}
                              placeholder="Д"
                            />
                            <Form.Control
                              size="sm"
                              type="number"
                              min={1}
                              step={1}
                              style={{ width: 64 }}
                              value={row.sku_width}
                              onChange={(e) => setReceiptMetricsById((prev) => ({
                                ...prev,
                                [row.id]: { ...prev[row.id], sku_width: e.target.value },
                              }))}
                              placeholder="Ш"
                            />
                            <Form.Control
                              size="sm"
                              type="number"
                              min={1}
                              step={1}
                              style={{ width: 64 }}
                              value={row.sku_height}
                              onChange={(e) => setReceiptMetricsById((prev) => ({
                                ...prev,
                                [row.id]: { ...prev[row.id], sku_height: e.target.value },
                              }))}
                              placeholder="В"
                            />
                          </div>
                        </td>
                        <td style={{ width: 100 }}>
                          <Form.Control
                            size="sm"
                            type="number"
                            min={0.001}
                            step={0.001}
                            value={row.sku_weight_kg}
                            onChange={(e) => setReceiptMetricsById((prev) => ({
                              ...prev,
                              [row.id]: { ...prev[row.id], sku_weight_kg: e.target.value },
                            }))}
                            placeholder="кг"
                          />
                        </td>
                        <td>
                          {row.enriched_from_wb && (
                            <Badge bg="success">WB</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          )}
          <Form.Group className="mb-3">
            <Form.Label>Вариант списания</Form.Label>
            <div className="d-flex flex-column gap-2">
              <Form.Check
                type="radio"
                name="receiptVariant"
                id="receipt_warehouse_default"
                label="Со склада (2 ¥ / позиция)"
                checked={receiptVariant === 'warehouse_default'}
                onChange={() => setReceiptVariant('warehouse_default')}
              />
              <Form.Check
                type="radio"
                name="receiptVariant"
                id="receipt_pickup_default"
                label="С ПВЗ (5,5 ¥ / позиция)"
                checked={receiptVariant === 'pickup_default'}
                onChange={() => setReceiptVariant('pickup_default')}
              />
              <Form.Check
                type="radio"
                name="receiptVariant"
                id="receipt_custom"
                label="Своё значение"
                checked={receiptVariant === 'custom'}
                onChange={() => {
                  setReceiptVariant('custom');
                  setReceiptType('receipt_warehouse');
                  setReceiptAmount('2');
                }}
              />
            </div>
          </Form.Group>
          {receiptVariant === 'custom' && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Тип приёмки для своего значения</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  <Form.Check
                    type="radio"
                    name="receiptType"
                    id="receipt_type_warehouse_custom"
                    label="Со склада"
                    checked={receiptType === 'receipt_warehouse'}
                    onChange={() => setReceiptType('receipt_warehouse')}
                  />
                  <Form.Check
                    type="radio"
                    name="receiptType"
                    id="receipt_type_pickup_custom"
                    label="С ПВЗ"
                    checked={receiptType === 'receipt_pickup'}
                    onChange={() => setReceiptType('receipt_pickup')}
                  />
                </div>
              </Form.Group>
              <Form.Group>
                <Form.Label>Своя сумма за позицию (¥)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={receiptAmount}
                  onChange={(e) => setReceiptAmount(e.target.value)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              receiptPreflightAbortRef.current?.abort();
              receiptPreflightAbortRef.current = null;
              setShowReceiptModal(false);
              setReceiptPreflightError('');
              setReceiptGoodsReturnSync(null);
              setReceiptMetricsById({});
            }}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={handleReceiptModalConfirm}
            disabled={
              receiptSaving
              || !receiptMetricsFormValid(receiptMetricsById)
              /* Не добавлять receiptPreflightError / receiptPreflightLoading — биллинг не зависит от WB preflight. */
            }
          >
            {receiptSaving ? <Spinner animation="border" size="sm" /> : 'Создать списание и сменить статус'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Prepare return modal: when setting status to sent_to_seller — confirm amount per item */}
      <Modal show={showPrepareReturnModal} onHide={() => setShowPrepareReturnModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Подготовка к отправке продавцу</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            Будет создано списание за подготовку к отправке продавцу (по одной записи на каждую позицию заказа). Измените сумму при необходимости (¥ за позицию).
          </p>
          <Form.Group>
            <Form.Label>Сумма за позицию (¥)</Form.Label>
            <Form.Control
              type="number"
              step="0.01"
              min="0.01"
              value={prepareReturnAmount}
              onChange={(e) => setPrepareReturnAmount(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPrepareReturnModal(false)}>Отмена</Button>
          <Button variant="primary" onClick={handlePrepareReturnModalConfirm} disabled={prepareReturnSaving}>
            {prepareReturnSaving ? <Spinner animation="border" size="sm" /> : 'Создать списание и перевести в «Отправлен продавцу»'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Barcode selection: before sale transfer or WH label when sku_barcode is empty */}
      <Modal
        show={showSaleBarcodeModal}
        onHide={() => {
          if (saleBarcodeSaving) return;
          setShowSaleBarcodeModal(false);
          setPendingSaleAction(null);
          setPendingLabelOrder(null);
        }}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Выбор штрихкода (WB)</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            У товара нет штрихкода в системе. Выберите вариант из карточки WB (размер и chrtID).
            {saleBarcodePreflightLoading && (
              <span className="ms-2">
                <Spinner animation="border" size="sm" />
              </span>
            )}
          </p>
          {saleBarcodePreflightError && (
            <Alert variant="danger" className="py-2">{saleBarcodePreflightError}</Alert>
          )}
          {(saleBarcodePreflight ? preflightBarcodeModalRows(saleBarcodePreflight) : []).map((row) => (
              <Card key={row.id} className="mb-3 border-secondary">
                <Card.Body className="py-2">
                  <div className="fw-semibold small mb-1">
                    {row.sku_code || `Позиция #${row.id}`}
                  </div>
                  {row.tech_size_hint ? (
                    <div className="text-muted small mb-2">
                      Размер из возврата WB: <strong>{row.tech_size_hint}</strong>
                    </div>
                  ) : null}
                  {row.error === 'no_barcodes_in_wb_card' ? (
                    <Alert variant="warning" className="py-2 small">
                      В карточке WB нет штрихкодов в sizes[].skus — введите sku_barcode вручную.
                    </Alert>
                  ) : null}
                  {row.error === 'wb_card_not_found' ? (
                    <Alert variant="warning" className="py-2 small">
                      Карточка не найдена в Content API — проверьте nmID/артикул или введите штрихкод вручную.
                    </Alert>
                  ) : null}
                  <WbCardSummaryPanel wbCard={row.wb_card} searchKey={row.search_key} />
                  {row.needs_selection ? (
                    <Form.Group className="mb-0">
                      {(row.barcode_options || []).map((opt) => (
                        <Form.Check
                          key={`${row.id}-${opt.barcode}`}
                          type="radio"
                          name={`sale-barcode-${row.id}`}
                          id={`sale-barcode-${row.id}-${opt.barcode}`}
                          className="mb-1"
                          label={
                            <>
                              {opt.label || opt.barcode}
                              {opt.recommended ? (
                                <Badge bg="success" className="ms-2">рекомендуется</Badge>
                              ) : null}
                            </>
                          }
                          checked={saleBarcodeSelections[row.id] === opt.barcode}
                          onChange={() =>
                            setSaleBarcodeSelections((prev) => ({ ...prev, [row.id]: opt.barcode }))
                          }
                        />
                      ))}
                    </Form.Group>
                  ) : null}
                  {row.needs_manual_barcode ? (
                    <Form.Group className="mb-0">
                      <Form.Label className="small mb-1">Штрихкод (sku_barcode)</Form.Label>
                      <Form.Control
                        type="text"
                        value={saleBarcodeSelections[row.id] || ''}
                        onChange={(e) =>
                          setSaleBarcodeSelections((prev) => ({ ...prev, [row.id]: e.target.value }))
                        }
                        placeholder="Введите штрихкод вручную"
                      />
                    </Form.Group>
                  ) : null}
                </Card.Body>
              </Card>
            ))}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            disabled={saleBarcodeSaving}
            onClick={() => {
              setShowSaleBarcodeModal(false);
              setPendingSaleAction(null);
              setPendingLabelOrder(null);
            }}
          >
            Отмена
          </Button>
          <Button
            variant="primary"
            onClick={saveSaleBarcodeSelectionsAndContinue}
            disabled={saleBarcodeSaving || saleBarcodePreflightLoading}
          >
            {saleBarcodeSaving ? (
              <Spinner animation="border" size="sm" />
            ) : pendingSaleAction === 'label' ? (
              'Сохранить и сгенерировать этикетку'
            ) : (
              'Сохранить и передать в продажу'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Billing modal: after order set to "sent to seller", show created expenses and allow edit */}
      <Modal show={showBillingModal} onHide={() => { setShowBillingModal(false); closeBillingExpenseEdit(); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Списания по заказу созданы</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted mb-3">
            По заказу <strong>{billingOrderNo}</strong> созданы списания. Ниже указаны тип услуги, дата начисления и время создания. При необходимости измените сумму и укажите причину.
          </p>
          {billingExpenses.length === 0 ? (
            <p className="mb-0">Нет списаний по этому заказу.</p>
          ) : (
            <Table size="sm" bordered responsive>
              <thead>
                <tr>
                  <th>Тип списания</th>
                  <th>Дата начисления</th>
                  <th>Создано</th>
                  <th>Товар</th>
                  <th>Кол-во WH</th>
                  <th>Сумма (¥)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {billingExpenses.map((exp) => (
                  <tr key={exp.id}>
                    <td>{exp.type_display || exp.type || '—'}</td>
                    <td>{exp.billing_date ? exp.billing_date : (exp.created_at ? exp.created_at.slice(0, 10) : '—')}</td>
                    <td>{exp.created_at ? new Date(exp.created_at).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</td>
                    <td>{exp.product != null && exp.product !== '' ? exp.product : exp.barcode || '—'}</td>
                    <td>{exp.wh_stock_units != null ? exp.wh_stock_units : '—'}</td>
                    <td>{exp.amount}</td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => openBillingExpenseEdit(exp)}>
                        Изменить сумму
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
          {editingBillingExpense && (
            <Card className="mt-3 border-primary">
              <Card.Body>
                <Card.Title as="h6">Редактировать списание</Card.Title>
                {billingSaveError && <Alert variant="danger" className="py-2">{billingSaveError}</Alert>}
                <Form.Group className="mb-2">
                  <Form.Label>Сумма (¥), списание — отрицательная</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={billingEditAmount}
                    onChange={(e) => setBillingEditAmount(e.target.value)}
                  />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label>Причина изменения (обязательно при отличии от авто-суммы)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={billingEditReason}
                    onChange={(e) => setBillingEditReason(e.target.value)}
                    placeholder="Например: корректировка по договорённости"
                  />
                </Form.Group>
                <Button size="sm" variant="secondary" onClick={closeBillingExpenseEdit} disabled={billingSaving}>Отмена</Button>
                {' '}
                <Button size="sm" variant="primary" onClick={handleBillingExpenseSave} disabled={billingSaving}>
                  {billingSaving ? <Spinner animation="border" size="sm" /> : 'Сохранить'}
                </Button>
              </Card.Body>
            </Card>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowBillingModal(false); closeBillingExpenseEdit(); }}>
            Закрыть
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Attachment Modal */}
      <Modal show={showAttachmentModal} onHide={closeAttachmentModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t('adminOrders.addAttachmentTitle')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('adminOrders.attachmentType')}</Form.Label>
              <Form.Select
                value={attachmentType}
                onChange={(e) => {
                  setAttachmentType(e.target.value);
                  setAttachmentFiles([]);
                }}
              >
                <option value="photo">{t('adminOrders.photo')}</option>
                <option value="video">{t('adminOrders.video')}</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('adminOrders.file')}</Form.Label>
              <Form.Control
                key={attachmentType}
                type="file"
                multiple={attachmentType === 'photo'}
                accept={attachmentType === 'photo' ? 'image/*' : 'video/*'}
                onChange={(e) => {
                  const picked = e.target.files ? Array.from(e.target.files) : [];
                  setAttachmentFiles(attachmentType === 'video' ? picked.slice(0, 1) : picked);
                }}
                className="file-input-styled"
              />
              {attachmentType === 'photo' && attachmentFiles.length > 0 && (
                <Form.Text muted>
                  {t('adminOrders.attachmentFilesSelected', { count: attachmentFiles.length })}
                </Form.Text>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAttachmentModal}>
            {t('adminOrders.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleAttachmentUpload}
            disabled={attachmentFiles.length === 0 || uploadingAttachment}
          >
            {uploadingAttachment ? <Spinner animation="border" size="sm" /> : t('adminOrders.upload')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Photo Lightbox Modal — клик по тёмному фону закрывает просмотр */}
      <Modal 
        show={!!lightboxPhoto} 
        onHide={() => setLightboxPhoto(null)} 
        fullscreen
        className="photo-lightbox-modal"
        contentClassName="bg-transparent border-0"
      >
        <Modal.Body
          className="photo-lightbox-backdrop p-0 d-flex align-items-center justify-content-center"
          onClick={() => setLightboxPhoto(null)}
        >
          <div
            className="position-relative"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="link"
              className="lightbox-close-btn"
              onClick={() => setLightboxPhoto(null)}
              aria-label={t('common.close')}
            >
              <i className="bi bi-x-lg"></i>
            </Button>

            {lightboxPhotos.length > 1 && (
              <>
                <Button
                  variant="link"
                  className="lightbox-nav-btn lightbox-prev"
                  onClick={() => {
                    const newIndex = lightboxIndex === 0 ? lightboxPhotos.length - 1 : lightboxIndex - 1;
                    setLightboxIndex(newIndex);
                    setLightboxPhoto(lightboxPhotos[newIndex]);
                  }}
                >
                  <i className="bi bi-chevron-left"></i>
                </Button>
                <Button
                  variant="link"
                  className="lightbox-nav-btn lightbox-next"
                  onClick={() => {
                    const newIndex = lightboxIndex === lightboxPhotos.length - 1 ? 0 : lightboxIndex + 1;
                    setLightboxIndex(newIndex);
                    setLightboxPhoto(lightboxPhotos[newIndex]);
                  }}
                >
                  <i className="bi bi-chevron-right"></i>
                </Button>
              </>
            )}

            {lightboxPhoto && (
              <div className="lightbox-image-container">
                <img
                  src={lightboxPhoto.file_url}
                  alt={lightboxPhoto.file_name || t('adminOrders.photo')}
                  className="lightbox-image"
                />
              </div>
            )}

            {lightboxPhotos.length > 1 && (
              <div className="lightbox-counter">
                {lightboxIndex + 1} / {lightboxPhotos.length}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdminOrdersPage;

